<template>
  <vx-card title="Tabla de Estructura de Pagos" class="mt-5">
    <vs-button size="small" :color="generatePaymentButtonColor"
      :disabled="project.profile.payments.items.length > 0 && project.profile.payments.paymentRequested.length > 0"
      @click="generatePaymentButtonHandler">
      <template v-if="project.profile.payments.length === 0">Generar estructura de pagos</template>
      <template v-else>Cambiar la estructura de pagos</template>
    </vs-button>

    <div class="mt-5">
      <vs-table :data="project.profile.payments.items">
        <template slot="thead">
          <vs-th>{{ project.profile.prefix }}</vs-th>
          <vs-th>Nombre comercial</vs-th>
          <vs-th>Monto</vs-th>
          <vs-th>Porcentaje</vs-th>
          <vs-th>¿Cobra comisiones?</vs-th>
          <vs-th v-if="!project.isApplicantLoan()">Porcentaje de comisión</vs-th>
          <vs-th>Status</vs-th>
          <vs-th>Orden</vs-th>
          <vs-th>F. de solicitud</vs-th>
          <vs-th>Acción</vs-th>
        </template>
        <template slot-scope="{data}">
          <vs-tr v-for="(payment, index) in data" :key="index">
            <vs-td>{{ payment.profileId }}</vs-td>
            <vs-td>{{ payment.profileName }}</vs-td>
            <vs-td>{{ payment.amount | currency }}</vs-td>
            <vs-td>{{ payment.paymentPercentage | fixedPercentage }} %</vs-td>
            <vs-td>{{ payment.hasFee ? 'Sí' : 'No' }}</vs-td>
            <vs-td v-if="!project.isApplicantLoan()">{{ payment.feePercentage | fixedPercentage }} %</vs-td>
            <vs-td>
              <span class="label" :class="{
                'label-danger': payment.status === 'pending',
                'label-success': payment.status === 'requested' || payment.status === 'done',
              }">
                {{ payment.status | translate }}
              </span>
            </vs-td>
            <vs-td>{{ payment.order }}</vs-td>
            <vs-td>{{ payment.paymentRequestDate }}</vs-td>
            <vs-td>
              <template v-if="project.profile.payments.nextPayment !== null">
                <vs-button size="small"
                  :disabled="(!userIs('admin') && !userIs('project_operator')) || selectedBankAccount == null"
                  @click="requestPaymentDialogHandler(payment)"
                  v-if="payment.paymentRequestDate === null && project.profile.payments.nextPayment.order === payment.order">
                  Solicitar
                </vs-button>
              </template>
            </vs-td>
          </vs-tr>
          <vs-tr>
            <vs-td></vs-td>
            <vs-td></vs-td>
            <vs-td>{{ project.profile.payments.totalAmount | currency }}</vs-td>
            <vs-td></vs-td>
            <vs-td></vs-td>
            <vs-td colspan="5"></vs-td>
          </vs-tr>
        </template>
      </vs-table>

      <vs-popup :active.sync="isConfirmDialogActive" title="Confirmación de liberación de pago">
        <vs-button class="mt-2" @click="requestPaymentHandler()" v-if="!requestPaymentValidationFail()">
          Confirmar
        </vs-button>

        <vs-card v-else class="mt-5">
          <p>Se solicitá el 2fa por las siguientes razones: </p>
          <ul v-if="requestPaymentValidationFail()">
            <li v-if="project.completeEvidenceDate === null && project.profile.payments.nextPayment.order === 2">
              La evidencia del proyecto no ha sido completada.
            </li>
            <li v-if="project.fundDate === null">
              El proyecto aún se encuentra en fondeo.
            </li>
            <li v-if="project.contractUnVerified.length > 0 && project.profile.payments.nextPayment.order === 1">
              Hay {{ project.contractUnVerified.length }} {{ project.contractUnVerifiedText }} por verificar.
            </li>
          </ul>
          <vs-button class="mt-2" color="danger" @click="requestPaymentHandler2Fa()">
            Confirmar con 2FA
          </vs-button>
        </vs-card>

        <vs-table :data="supplierPaymentActions" class="mt-2">
          <template slot="thead">
            <th>Descripción</th>
          </template>
          <template slot-scope="{data}">
            <vs-tr v-for="(step, index) in supplierPaymentActions" :key="index">
              <vs-td>
                {{ step }}
              </vs-td>
            </vs-tr>
          </template>
        </vs-table>

        <otp-two-factor-authenticator ref="2fa" />
      </vs-popup>
    </div>
  </vx-card>
</template>

<script>
import OtpTwoFactorAuthenticator from "@components/Otp/OtpTwoFactorAuthenticator";
import { LoanProject } from "@/models/LoanPayment/Project";
import { is } from '../functions/userRoleBehavior';
import { mapState } from "vuex";
import { LoanBankAccount } from "@/models/LoanPayment/LoanBankAccount";

export default {
  name: "loan-payment-card",
  components: { OtpTwoFactorAuthenticator },
  props: {
    selectedBankAccount: {
      type: LoanBankAccount | null,
      required: true
    },
    project: {
      type: LoanProject,
      required: true
    }
  },
  filters: {
    fixedPercentage(value) {
      return (value * 100).toFixed(2);
    },
    translate(status) {
      if (status === "pending") {
        return "Pendiente";
      }
      if (status === "requested") {
        return "Solicitado"
      }
      if (status === "done") {
        return "Pagado"
      }
    }
  },
  computed: {
    ...mapState("auth", ["geolocation"]),
    generatePaymentButtonColor() {
      return this.project.profile.payments.paymentRequested.length === 0 ? 'primary' : 'danger';
    },
    contractUnVerifiedText() {
      return this.project.contractUnVerified > 1 ? 'contratos pendientes' : 'contrato pendiente'
    },
    ...mapState("auth", ["user"])
  },
  data: () => ({
    showDialog: false,
    isConfirmDialogActive: false,
    supplierPaymentActions: []
  }),
  methods: {
    userIs(role) {
      return is(role, this.user.user_roles);
    },
    generatePaymentButtonHandler() {
      this.$emit('generatePaymentButtonEvent')
    },
    async requestPaymentDialogHandler(payment) {
      this.currentPayment = payment;
      await this.requestPaymentSimulationHandler(payment);
    },
    async requestPaymentSimulationHandler(payment) {
      this.$vs.loading();
      try {
        const { data } = await axios.post(`/api/loan-payment/simulation/${this.project.id}/${payment.id}`, {
          bankAccountId: this.selectedBankAccount.id,
          geolocation: {
            latitude: this.geolocation.latitude,
            longitude: this.geolocation.longitude
          }
        })
        this.supplierPaymentActions = data.messages;
        this.isConfirmDialogActive = true;
        this.$vs.loading.close();
      } catch (e) {
        this.$vs.loading.close();
        if (e.response.status === 422) {
          swal("Error", e.response.data.error, "warning");
        }
        if (e.response.status === 400) {
          swal("Error", e.response.data.error, "warning");
        }
      }
    },
    requestPaymentHandler2Fa() {
      this.$refs["2fa"].show();
      this.$refs["2fa"].$once('confirm', (value) => {
        if (value) {
          this.requestPaymentHandler()
          this.$refs["2fa"].close();
          this.$emit("onRequested", this.project)
        }
      })
    },
    requestPaymentValidationFail() {
      if (this.project.profile.payments.nextPayment !== null) {
        if (this.project.profile.payments.nextPayment.order === 1) {
          return this.project.contractUnVerified.length > 0 ||
            this.project.fundDate === null;
        }
        if (this.project.profile.payments.nextPayment.order === 2) {
          return this.project.completeEvidenceDate === null ||
            this.project.fundDate === null;
        }
      }
      return false;
    },
    async requestPaymentHandler() {
      this.$vs.loading();

      try {
        const { data } = await axios.post(`/api/loan-payment/request/${this.project.id}/${this.currentPayment.id}`, {
          bankAccountId: this.selectedBankAccount.id,
          geolocation: {
            latitude: this.geolocation.latitude,
            longitude: this.geolocation.longitude
          }
        })
        this.currentPayment.status = data.status;
        this.currentPayment.payment_request_date = data.payment_request_date;
        this.isConfirmDialogActive = false;
        this.$vs.loading.close();
        swal("Notificación", "El pago se solicitó correctamente", "success");
        this.$emit('onRequested', data)
      } catch (e) {
        this.$vs.loading.close();
        if (e.response.status === 422) {
          swal("Error", e.response.data.error, "warning");
        }
      }
    }
  }
}
</script>

<style scoped></style>
